import React, { useEffect } from "react";
import { useAuth, AUTH_PHASES } from "./AuthProvider";
import { navigate } from "../lib/navigate";
import { hasDeletionReceipt } from "../account/deletionStorage";

const ALLOWED_NEXT = new Set([
  "/account",
  "/account/profile",
  "/account/billing",
  "/account/usage",
  "/account/preferences",
  "/account/privacy",
  "/account/deletion-status",
  "/pricing",
  "/stratifypro",
  "/checkout/success",
  "/checkout/cancel",
]);

export function allowlistedReturnPath(raw) {
  if (!raw || typeof raw !== "string") return "/account";
  const path = raw.startsWith("/") ? raw.split("?")[0] : "";
  if (!path || path.includes("//") || path.includes("\\") || path.includes("://")) {
    return "/account";
  }
  if (ALLOWED_NEXT.has(path)) return path;
  return "/account";
}

export default function RequireAuth({ children, allowClosing = false, allowAnonymousReceipt = false }) {
  const auth = useAuth();
  const anonymousReceipt = allowAnonymousReceipt && hasDeletionReceipt();

  useEffect(() => {
    if (!auth.ready) return;
    if (auth.phase === AUTH_PHASES.signedOut || auth.phase === AUTH_PHASES.authenticating) {
      if (anonymousReceipt) return;
      const next = allowlistedReturnPath(window.location.pathname || "/account");
      navigate(`/sign-in?next=${encodeURIComponent(next)}`, { replace: true });
    }
  }, [auth.ready, auth.phase, anonymousReceipt]);

  useEffect(() => {
    if (auth.phase === AUTH_PHASES.deletionRequested && !allowClosing) {
      navigate("/account/deletion-status", { replace: true });
    }
  }, [auth.phase, allowClosing]);

  if (!auth.ready || auth.phase === AUTH_PHASES.initializing) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16 text-sm text-muted" role="status">
        Loading your account…
      </div>
    );
  }

  if (auth.phase === AUTH_PHASES.signedOut || auth.phase === AUTH_PHASES.authenticating) {
    if (anonymousReceipt) return children;
    return (
      <div className="mx-auto max-w-3xl px-5 py-16 text-sm text-muted" role="status">
        Redirecting to sign in…
      </div>
    );
  }

  if (auth.phase === AUTH_PHASES.deleted) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <h1 className="text-2xl font-semibold text-ink">This account is closed</h1>
        <p className="mt-3 text-sm text-muted">Sign in is no longer available for this account.</p>
      </div>
    );
  }

  if (auth.phase === AUTH_PHASES.deletionRequested && !allowClosing) {
    return null;
  }

  if (auth.phase === AUTH_PHASES.error) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <h1 className="text-2xl font-semibold text-ink">We couldn’t load your account</h1>
        <p className="mt-3 text-sm text-muted">{auth.errorMessage || "Try again."}</p>
        <button
          type="button"
          onClick={() => auth.retryLifecycle()}
          className="mt-6 inline-flex min-h-11 items-center rounded-full bg-ink px-4 text-sm font-semibold text-white"
        >
          Try again
        </button>
      </div>
    );
  }

  return children;
}
